import { type RouteLocation, type Router, useRouter } from 'vue-router'
import { getWindow } from '@/helpers/globals'
import { type Routes, useRoutes } from '@/router/routes'
import { useHost } from '@/plugins/current-url'
import { computed, type ComputedRef, type Ref, toValue } from 'vue'
import {
    type ReleasedUrlsChecker,
    useReleasedUrls,
} from '@/plugins/released-urls/released-urls'

export type HrefLocalizer = (text: string) => string

export function useLocalizedHrefs(
    text: Ref<string> | (() => string)
): ComputedRef<string> {
    const localize = useHrefLocalizer()

    return computed(() => localize(toValue(text)))
}

export function useHrefLocalizer(): HrefLocalizer {
    const router = useRouter()
    const routes = useRoutes()
    const host = useHost()
    const isReleasedUrl = useReleasedUrls()

    return (text: string) =>
        localizeHrefs(text, host, router, routes, isReleasedUrl)
}

function localizeHrefs(
    text: string,
    host: string,
    router: Router,
    routes: Routes,
    isReleasedUrl: ReleasedUrlsChecker
): string {
    const window = getWindow()

    if (window && !window.routerPush) {
        window.routerPush = function (element: HTMLAnchorElement): boolean {
            const url = new URL(element.href)
            const resolved = router.resolve(
                `${url.pathname}${url.search}${url.hash}`
            )

            if (isKnownRoute(resolved)) {
                router.push(
                    routes.localize({
                        name: resolved.name,
                        hash: resolved.hash,
                        params: resolved.params,
                        query: resolved.query,
                    })
                )

                return false
            }

            return true
        }
    }

    const localizedText = text.replaceAll(
        /href="(.*?)"/g,
        (_matched: string, href: string): string => {
            if (
                href.startsWith('http') ||
                href.startsWith('https') ||
                href.startsWith('//')
            ) {
                // Handles stuff like https://127.0.0.1:3000/path?asdf=fdsa#test
                try {
                    const url = new URL(href)

                    // Do not touch the hardcoded taylorandhart.com check
                    // It is there because literally ALL content on the CMS is using full URIs
                    if (host !== url.host && 'taylorandhart.com' !== url.host) {
                        // Not on this host, use as-is
                        return `href="${href}"`
                    }

                    href = `${url.pathname}${url.search}${url.hash}`
                } catch {
                    // Do nothing, let it just fail silently
                }
            }

            if (href.startsWith('mailto:')) {
                // mailto: use as-is
                return `href="${href}"`
            }

            if (href.startsWith('tel:')) {
                // phone: use as-is
                return `href="${href}"`
            }

            if (href.startsWith('sms:')) {
                // sms: use as-is
                return `href="${href}"`
            }

            const route = router.resolve(href)

            if (!isKnownRoute(route)) {
                // Did not match any route, use it as-is
                return `href="${href}"`
            }

            // Resolve a localized route config
            const fullPath = router.resolve(
                routes.localize({
                    name: route.name,
                    hash: route.hash,
                    params: route.params,
                    query: route.query,
                })
            ).fullPath

            if (!isReleasedUrl(route)) {
                // Use regular document nav for non-released
                return `href="${fullPath}"`
            }

            // IT'S THE FINAL COUNTDOWN
            // https://taylorandhart.slack.com/archives/GM1LLD9J8/p1708304080038699
            return `href="${fullPath}" onclick="return 'routerPush' in window ? routerPush(this) : true"`
        }
    )

    return addAriaLabelsForBlankTargetLinks(localizedText)
}

function addAriaLabelsForBlankTargetLinks(text: string): string {
    return text.replace(
        /<a\b([^>]*\btarget\s*=\s*(['"])_blank\2[^>]*)>([\s\S]*?)<\/a>/gi,
        (
            _matched: string,
            attributes: string,
            _quote: string,
            body: string
        ) => {
            if (/\baria-label\s*=/.test(attributes)) {
                return `<a${attributes}>${body}</a>`
            }

            const linkText = body
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
            const ariaLabel = escapeHtmlAttribute(
                `${linkText ? `${linkText} ` : ''}- opens in new tab`
            )

            return `<a${attributes} aria-label="${ariaLabel}">${body}</a>`
        }
    )
}

function escapeHtmlAttribute(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

export function isKnownRoute(
    route: RouteLocation & {
        href: string
    }
): route is Omit<typeof route, 'name'> & { name: string } {
    return undefined !== route.name && null !== route.name
}
